import PubSub from "underpub-js";
import PopupPositioner from "./PopupPositioner";

/**
 * BasePopup
 * Base class for popup components (PopupUnit, PopupItem, etc.)
 * Handles common functionality like floor-based visibility, hover interactions, and positioning
 */
var BasePopup = function(options) {
	this.options = {
		$el: false,
		baseClass: '',
		dotSelector: '',      // CSS selector for the dot element
		popupSelector: '',    // CSS selector for the popup element
		pointerSelector: '',  // CSS selector for the pointer element (optional)
		componentName: ''     // Component name for logging (e.g., 'PopupUnit', 'PopupItem')
	};

	$.extend(true, this.options, options);

	// Cache jQuery elements
	this.$dot = null;
	this.$popup = null;
	this.$pointer = null;
	
	// Store floor index (1-based from data attribute)
	this.floorIndex = null;
	
	// Track if dot is visible for current floor
	this.isVisibleForCurrentFloor = false;
	
	// Track if event subscriptions have been set up
	this.subscriptionsSetup = false;
};

/**
 * Initialize the popup component
 */
BasePopup.prototype.init = function() {
	var self = this;

	// Get floor index from data attribute (1-based)
	var floorIndexAttr = this.options.$el.attr('data-floor-index');
	this.floorIndex = floorIndexAttr ? parseInt(floorIndexAttr, 10) : null;
	
	// Reduced logging - only log if there's an issue
	// if (this.options.componentName) {
	// 	console.log(this.options.componentName + ': Initialized', {
	// 		floorIndexAttr: floorIndexAttr,
	// 		floorIndex: this.floorIndex,
	// 		$el: this.options.$el[0]
	// 	});
	// }
	
	if (this.floorIndex === null || isNaN(this.floorIndex)) {
		if (this.options.componentName) {
			console.warn(this.options.componentName + ': No valid floorIndex found in data-floor-index attribute', {
				attr: floorIndexAttr,
				parsed: this.floorIndex,
				element: this.options.$el[0]
			});
		}
	}

	// Find the dot and popup elements using provided selectors
	this.$dot = this.options.$el.find(this.options.dotSelector);
	this.$popup = this.options.$el.find(this.options.popupSelector);
	
	// Find pointer element if selector is provided
	if (this.options.pointerSelector) {
		this.$pointer = this.options.$el.find(this.options.pointerSelector);
	}

	// Set initial state - dot hidden, popup hidden
	this.$dot.css({
		'display': 'none',
		'opacity': '0'
	});

	this.$popup.css({
		'opacity': '0',
		'visibility': 'hidden',
		'pointer-events': 'none'
	});

	// Disable pointer events on container initially (will be enabled when dot is shown for active floor)
	this.options.$el.css('pointer-events', 'none');

	// Set up hover event listeners (remove any existing first to prevent duplicates)
	this.options.$el.off('mouseenter mouseleave');
	this.setupEventListeners();
	
	// Subscribe to floor change events (only once per instance)
	if (!this.subscriptionsSetup) {
		this.subscribeToFloorChanges();
		this.subscriptionsSetup = true;
	}
	
	// Request current floor in case we initialized after the initial floor was set
	this.checkCurrentFloor();
};

/**
 * Set up hover event listeners
 */
BasePopup.prototype.setupEventListeners = function() {
	var self = this;

	// Show popup on hover (dot is already visible if it's for the current floor)
	this.options.$el.on('mouseenter', function(e) {
		// Stop event propagation to prevent triggering other popups
		e.stopPropagation();
		self.showPopup();
	});

	// Hide popup on mouse leave (dot stays visible based on floor)
	this.options.$el.on('mouseleave', function(e) {
		// Stop event propagation
		e.stopPropagation();
		self.hidePopup();
	});
};

/**
 * Hide all other popups on the same floor
 */
BasePopup.prototype.hideOtherPopups = function() {
	var self = this;
	
	// Find all popup elements on the same floor
	var $allPopups = $('[data-floor-index="' + this.floorIndex + '"]');
	
	$allPopups.each(function() {
		var $otherPopup = $(this);
		
		// Skip this popup instance
		if ($otherPopup[0] === self.options.$el[0]) {
			return;
		}
		
		// Find the popup wrapper in the other popup and hide it
		var $otherPopupWrapper = $otherPopup.find('.popup-unit_wrapper, .popup-item_wrapper');
		if ($otherPopupWrapper.length) {
			$otherPopupWrapper.css({
				'opacity': '0',
				'visibility': 'hidden',
				'pointer-events': 'none'
			});
			// Reset z-index
			$otherPopup.css('z-index', '');
		}
	});
};

/**
 * ShowDot: Display the dot and fade it in
 * Changes display from none to flex, and transitions opacity from 0 to 1
 */
BasePopup.prototype.showDot = function() {
	if (!this.$dot || this.$dot.length === 0) return;

	// Set display to flex first
	this.$dot.css('display', 'flex');

	// Force reflow to ensure display change is applied
	this.$dot[0].offsetHeight;

	// Transition opacity to 1
	this.$dot.css({
		'opacity': '1',
		'transition': 'opacity 0.25s ease-out'
	});
};

/**
 * HideDot: Hide the dot and fade it out
 * Reverses the showDot interaction
 */
BasePopup.prototype.hideDot = function() {
	var self = this;

	if (!this.$dot || this.$dot.length === 0) return;

	// Transition opacity to 0
	this.$dot.css({
		'opacity': '0',
		'transition': 'opacity 0.2s ease-in'
	});

	// After transition completes, set display to none
	setTimeout(function() {
		if (self.$dot.css('opacity') === '0') {
			self.$dot.css('display', 'none');
		}
	}, 200);
};

/**
 * Adjust popup position to keep it within viewport bounds
 */
BasePopup.prototype.adjustPopupPosition = function() {
	if (!this.$popup || this.$popup.length === 0) return;
	if (!this.$dot || this.$dot.length === 0) return;

	// Use PopupPositioner utility
	var positioner = new PopupPositioner({
		$popup: this.$popup,
		$container: this.options.$el,
		margin: 10,
		debug: true // Enable debug logging
	});

	var positionResult = positioner.calculatePosition();
	if (positionResult) {
		positioner.applyPosition(positionResult);
		
		// Hide pointer if popup was adjusted horizontally (transformX is non-zero)
		// This prevents the pointer from appearing in the middle of the popup
		if (this.$pointer && this.$pointer.length > 0) {
			if (Math.abs(positionResult.transformX) > 0.1) {
				this.$pointer.css('display', 'none');
			} else {
				this.$pointer.css('display', '');
			}
		}
	}
};

/**
 * ShowPopup: Fade in the popup content
 */
BasePopup.prototype.showPopup = function() {
	if (!this.$popup || this.$popup.length === 0) return;
	
	// Only show popup if dot is visible for current floor
	if (!this.isVisibleForCurrentFloor) {
		return;
	}

	// Hide all other popups on the same floor first
	this.hideOtherPopups();

	// Adjust position before showing
	this.adjustPopupPosition();

	// Raise z-index of container to ensure it's above sibling popups and their dots
	this.options.$el.css('z-index', '102');

	// Enable pointer events and make popup visible
	// First, clear any existing transition and set initial state
	this.$popup.css({
		'visibility': 'visible',
		'opacity': '0',
		'pointer-events': 'auto',
		'transition': 'none'
	});

	// Force reflow to ensure the initial state is applied
	this.$popup[0].offsetHeight;

	// Use requestAnimationFrame to ensure browser processes the initial state
	// Then set transition and opacity in the next frame to trigger the animation
	var self = this;
	requestAnimationFrame(function() {
		requestAnimationFrame(function() {
			self.$popup.css({
				'transition': 'opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
				'opacity': '1'
			});
		});
	});
};

/**
 * HidePopup: Fade out the popup content
 */
BasePopup.prototype.hidePopup = function() {
	var self = this;

	if (!this.$popup || this.$popup.length === 0) return;

	// Disable pointer events immediately to prevent re-opening during fade-out
	this.$popup.css('pointer-events', 'none');

	// Fade out
	this.$popup.css({
		'opacity': '0',
		'transition': 'opacity 0.2s cubic-bezier(0.4, 0, 1, 1)'
	});

	// After transition completes, hide visibility, disable pointer events, and reset pointer
	setTimeout(function() {
		if (self.$popup.css('opacity') === '0') {
			self.$popup.css({
				'visibility': 'hidden',
				'pointer-events': 'none'
			});
			
			// Reset pointer display so it shows correctly next time
			if (self.$pointer && self.$pointer.length > 0) {
				self.$pointer.css('display', '');
			}
			
			// Reset container z-index back to default
			self.options.$el.css('z-index', '');
		}
	}, 200);
};

/**
 * Subscribe to floor change events from AvailabilityMap
 */
BasePopup.prototype.subscribeToFloorChanges = function() {
	var self = this;
	var componentName = this.options.componentName || 'BasePopup';
	
	// Subscribe to floor change events (1-based floor index)
	PubSub.subscribe('availabilityMap:floorChanged', function(msg) {
		// underpub-js passes data as the first argument (msg), not as a second parameter
		var eventData = msg && msg.data ? msg.data : msg;
		
		if (!eventData || typeof eventData.floorIndex === 'undefined') {
			if (componentName) {
				console.warn(componentName + ': floorChanged event missing floorIndex', {msg: msg, eventData: eventData});
			}
			return;
		}
		
		var activeFloorIndex = parseInt(eventData.floorIndex, 10); // 1-based
		
		// Only proceed if we have a valid floorIndex
		if (!self.floorIndex || isNaN(self.floorIndex) || isNaN(activeFloorIndex)) {
			if (componentName) {
				console.warn(componentName + ': Invalid floorIndex comparison', {
					selfFloorIndex: self.floorIndex,
					activeFloorIndex: activeFloorIndex
				});
			}
			return;
		}
		
		// Mismatch is expected - popups on different floors will not match
		// No need to log this as it's normal behavior
		
		// Check if this popup belongs to the active floor (strict comparison)
		if (self.floorIndex === activeFloorIndex) {
			// Show dot for this floor
			self.showDotForFloor();
			self.isVisibleForCurrentFloor = true;
		} else {
			// Hide dot for other floors
			// If popup is currently open, close it first
			if (self.$popup && self.$popup.css('opacity') !== '0') {
				self.hidePopup();
			}
			self.hideDotForFloor();
			self.isVisibleForCurrentFloor = false;
		}
	});
	
	// Subscribe to initial floor set event
	PubSub.subscribe('availabilityMap:initialFloorSet', function(msg) {
		// underpub-js passes data as the first argument (msg), not as a second parameter
		var eventData = msg && msg.data ? msg.data : msg;
		
		if (!eventData || typeof eventData.floorIndex === 'undefined') {
			if (componentName) {
				console.warn(componentName + ': initialFloorSet event missing floorIndex', {msg: msg, eventData: eventData});
			}
			return;
		}
		
		var activeFloorIndex = parseInt(eventData.floorIndex, 10); // 1-based
		
		// Only proceed if we have a valid floorIndex
		if (!self.floorIndex || isNaN(self.floorIndex) || isNaN(activeFloorIndex)) {
			if (componentName) {
				console.warn(componentName + ': Invalid floorIndex comparison (initial)', {
					selfFloorIndex: self.floorIndex,
					activeFloorIndex: activeFloorIndex
				});
			}
			return;
		}
		
		// Mismatch is expected - popups on different floors will not match
		// No need to log this as it's normal behavior
		
		if (self.floorIndex === activeFloorIndex) {
			self.showDotForFloor();
			self.isVisibleForCurrentFloor = true;
		} else {
			// If popup is currently open, close it first
			if (self.$popup && self.$popup.css('opacity') !== '0') {
				self.hidePopup();
			}
			self.hideDotForFloor();
			self.isVisibleForCurrentFloor = false;
		}
	});
	
	// Subscribe to current floor response (for late-initializing popups)
	PubSub.subscribe('availabilityMap:currentFloor', function(msg) {
		// underpub-js passes data as the first argument (msg), not as a second parameter
		var eventData = msg && msg.data ? msg.data : msg;
		
		if (!eventData || typeof eventData.floorIndex === 'undefined') return;
		
		var activeFloorIndex = parseInt(eventData.floorIndex, 10); // 1-based
		
		// Only proceed if we have a valid floorIndex
		if (!self.floorIndex || isNaN(self.floorIndex) || isNaN(activeFloorIndex)) return;
		
		if (self.floorIndex === activeFloorIndex) {
			self.showDotForFloor();
			self.isVisibleForCurrentFloor = true;
		} else {
			// If popup is currently open, close it first
			if (self.$popup && self.$popup.css('opacity') !== '0') {
				self.hidePopup();
			}
			self.hideDotForFloor();
			self.isVisibleForCurrentFloor = false;
		}
	});
};

/**
 * Check the current floor by requesting it from AvailabilityMap
 * This handles the case where popups initialize after the initial floor is set
 */
BasePopup.prototype.checkCurrentFloor = function() {
	// Only check if we have a valid floorIndex
	if (!this.floorIndex || isNaN(this.floorIndex)) {
		return;
	}
	
	// Request current floor from AvailabilityMap
	PubSub.publish('availabilityMap:requestCurrentFloor');
	
	// Also check after a short delay to ensure AvailabilityMap is ready
	var self = this;
	setTimeout(function() {
		PubSub.publish('availabilityMap:requestCurrentFloor');
	}, 100);
};

/**
 * Show dot for floor visibility (without hover interaction)
 */
BasePopup.prototype.showDotForFloor = function() {
	if (!this.$dot || this.$dot.length === 0) {
		if (this.options.componentName) {
			console.warn(this.options.componentName + ': Dot element not found', {
				floorIndex: this.floorIndex,
				$dot: this.$dot
			});
		}
		return;
	}
	
	// Enable pointer events on container so it can receive hover events
	this.options.$el.css('pointer-events', 'auto');
	
	// Only show if not already visible from hover
	var currentOpacity = parseFloat(this.$dot.css('opacity')) || 0;
	if (currentOpacity < 1 || this.$dot.css('display') === 'none') {
		// Set display first
		this.$dot.css('display', 'flex');
		
		// Force reflow to ensure display change is applied
		this.$dot[0].offsetHeight;
		
		// Set opacity with transition
		this.$dot.css({
			'opacity': '1',
			'transition': 'opacity 0.25s ease-out'
		});
	}
};

/**
 * Hide dot for floor visibility (without hover interaction)
 */
BasePopup.prototype.hideDotForFloor = function() {
	var self = this;
	
	if (!this.$dot || this.$dot.length === 0) return;
	
	// Disable pointer events on container to prevent hover events when dot is hidden
	this.options.$el.css('pointer-events', 'none');
	
	// Always hide the dot when switching floors (don't check popup state)
	// The popup will be hidden separately via hidePopup() if needed
	this.$dot.css({
		'opacity': '0',
		'transition': 'opacity 0.2s ease-in'
	});
	
	setTimeout(function() {
		if (self.$dot.css('opacity') === '0') {
			self.$dot.css('display', 'none');
		}
	}, 200);
};

export default BasePopup;

